import type { CSSProperties, ReactNode, Ref } from 'react';
import classes from './SelectComponent.module.css';
import type {
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
  FlexProps,
  MantineRadius,
  MantineStyleProp,
  SelectProps,
} from '@mantine/core';
import { Flex, Select, useMantineTheme, Text, em } from '@mantine/core';
import { Icon } from '@/components/Icon/Icon';
import upperFirst from 'lodash/upperFirst';
import { HelpLink } from '@/components/Link/HelpLink';
import { useClientTerminology } from '@/hooks/useClientTerminology';
import { Check, ChevronDown } from 'react-feather';
import { useMergedClassNames } from '@/hooks/useMergedClassNames';
import { matchPath, useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';

type SelectOption = ComboboxItem & { url?: string };
type SelectOptionGroup<O extends SelectOption> = { group: string; items: O[] };

interface Props<O extends SelectOption> extends Omit<
  SelectProps,
  'data' | 'onChange' | 'renderOption' | 'value'
> {
  /**
   * Title for analysis select
   */
  title?: string;
  /**
   * Select options — flat array or grouped `{ group, items }[]`
   */
  options: (O | SelectOptionGroup<O>)[];
  selected: string;
  handleChange: (value: O['value']) => void;
  renderOption?: (item: ComboboxLikeRenderOptionInput<O>) => ReactNode;
  style?: CSSProperties;
  /**
   * Cooresponds to Mantine Select's allowDeselect prop
   */
  allowDeselect?: boolean;
  leftSection?: ReactNode;
  classNames?: Partial<ClassNames>;
  textStyles?: MantineStyleProp;
  radius?: MantineRadius;
  'data-testid'?: string;
}

interface ItemProps extends FlexProps {
  icon?: ReactNode;
  label: string;
  selected: boolean;
  value: string;
  url?: string;
  textStyles?: MantineStyleProp;
  'data-select-testid'?: string;
  ref?: Ref<HTMLDivElement>;
}

type ClassNames = {
  dropdown?: string;
  selectInput?: string;
  wrapper?: string;
  root?: string;
  title?: string;
  label?: string;
};

export const SelectItem = ({
  url,
  label,
  textStyles,
  id,
  'data-select-testid': selectTestId,
  ref,
  ...others
}: ItemProps) => {
  const { selected } = others;
  const intl = useClientTerminology();
  const theme = useMantineTheme();
  return (
    <Flex
      ref={ref}
      align="center"
      gap="lg"
      justify="space-between"
      w="100%"
      data-testid={id}
      data-select-testid={selectTestId}
    >
      <Flex gap="2px">
        <Flex w={24} h={24} display="flex" align="center" justify="center">
          {selected && <Icon size={16} Component={Check} />}
        </Flex>

        <Text
          style={{
            color: theme.other.mainColor.foreground.primary,
            ...theme.other.typography.variants.bodySmallRegular,
            ...textStyles,
            whiteSpace: 'normal',
          }}
        >
          {upperFirst(label)}
        </Text>
      </Flex>

      {url && (
        <HelpLink
          text={intl.formatMessage({ id: 'list.learnMore' })}
          link={url}
          textSize="sm"
        />
      )}
    </Flex>
  );
};

export const SelectComponent = <O extends SelectOption>({
  title,
  options,
  selected,
  allowDeselect,
  handleChange,
  style,
  leftSection,
  classNames,
  textStyles,
  inputContainer,
  renderOption: renderOptionProp,
  comboboxProps: comboboxPropsProp,
  'data-testid': testId,
  ...props
}: Props<O>) => {
  const theme = useMantineTheme();
  const isVerySmallScreen = useMediaQuery(`(max-width: ${em(425)})`);

  const flatOptions = options.flatMap((option) =>
    'items' in option ? option.items : [option]
  );
  const findOption = (value: string | null) =>
    flatOptions.find((option) => option.value === value);
  const hasUrls = flatOptions.some((option) => option.url);

  const mergedClasses = useMergedClassNames(classes, classNames);

  const location = useLocation();
  const isDashboard = matchPath('/dashboard', location.pathname) !== null;
  const rightSection = props.rightSection ? (
    props.rightSection
  ) : (
    <Icon
      Component={ChevronDown}
      size={16}
      color={theme.other.mainColor.foreground.tertiary}
    />
  );

  const selectComponent = (
    <Select
      {...props}
      data-testid={testId}
      allowDeselect={allowDeselect}
      renderOption={({ option, checked }) => {
        const match = findOption(option.value);
        if (!match) return null;
        if (renderOptionProp)
          return renderOptionProp({ option: match, checked });
        return (
          <SelectItem
            id={match.value}
            data-select-testid={testId}
            textStyles={textStyles}
            url={match.url}
            label={match.label}
            value={match.value}
            selected={checked ?? false}
          />
        );
      }}
      inputContainer={inputContainer}
      value={selected}
      onChange={(value) => {
        const option = findOption(value);
        if (option) handleChange(option.value);
      }}
      data={options}
      comboboxProps={{
        ...comboboxPropsProp,
        ...(isVerySmallScreen ? { width: '300px' } : {}),
      }}
      rightSection={rightSection}
      // Adding type="button" to prevent browser autofill by showing the dropdown as a button
      // rather than a select input.
      type="button"
      {...(leftSection ? { leftSection: leftSection } : {})}
      classNames={{
        dropdown: `${mergedClasses.dropdown} ${
          hasUrls ? classes.dropdownWide : ''
        }`,
        input: `${mergedClasses.input} ${isDashboard ? mergedClasses.dashboard : ''}`,
        root: mergedClasses.root,
        section: mergedClasses.section,
        option: mergedClasses.option,
      }}
    />
  );

  return title ? (
    <Flex
      gap={8}
      align="center"
      bg={theme.other.mainColor.background.primary}
      style={style}
      className={mergedClasses.selectWrapper}
    >
      <Text
        className={mergedClasses.title}
        style={{
          flexShrink: 0,
          ...theme.other.typography.variants.bodySmallRegular,
          color: theme.other.mainColor.foreground.secondary,
        }}
      >
        {title}
      </Text>
      {selectComponent}
    </Flex>
  ) : (
    selectComponent
  );
};
